/**
 * Year Zero Engine Integration for Lucid Cyber RPG
 * 
 * Implements core mechanics from Free League's Year Zero Engine:
 * - Stress and trauma system tied to karma choices
 * - Attribute + Skill dice pools
 * - Push mechanics for rerolls
 * - Mutations that evolve with story progression
 * - Equipment with karma-dependent abilities
 */

import { v4 as uuidv4 } from 'uuid';

export interface YearZeroAttribute {
  name: 'Strength' | 'Agility' | 'Wits' | 'Empathy';
  value: number; // 2-5
  current: number; // May be reduced by damage/stress
}

export interface YearZeroSkill {
  name: string;
  value: number; // 0-5
  attribute: YearZeroAttribute['name'];
  description: string;
  karmaModifier?: number; // Bonus/penalty based on karma alignment
}

export interface YearZeroTrauma {
  id: string;
  name: string;
  description: string;
  effect: string;
  karmaThreshold: number; // Karma level that triggered this trauma
  isActive: boolean;
  causedBy?: string; // Choice that caused the trauma
}

export interface YearZeroMutation {
  id: string;
  name: string;
  description: string;
  effect: string;
  karmaRange: [number, number]; // [min, max] karma to maintain this mutation
  evolutionLevel: number; // 1-5, evolves with story progression
  isActive: boolean;
}

export interface YearZeroItem {
  id: string;
  name: string;
  description: string;
  itemType: 'weapon' | 'armor' | 'gear' | 'cyberware' | 'artifact';
  bonus: number; // Dice bonus when used
  reliability: number; // 1-6, chance of breaking on 1
  karmaResonance?: number; // Works better with certain karma levels
  yearZeroRating: number; // Scrap value / complexity
  specialAbilities: string[];
  condition: 'broken' | 'worn' | 'intact' | 'pristine';
}

export interface YearZeroCharacter {
  id: string;
  name: string;
  attributes: YearZeroAttribute[];
  skills: YearZeroSkill[];
  stress: number; // Current stress level (0-10+)
  trauma: YearZeroTrauma[];
  mutations: YearZeroMutation[];
  equipment: YearZeroItem[];
  karmaAlignment: number;
  experience: number;
  storyProgression: number;
}

export interface DiceRoll {
  dice: number; // Number of d6 to roll
  successes: number; // 6s rolled
  ones: number; // 1s rolled (potential stress/damage)
  total: number;
  isPushed: boolean;
  description: string;
}

export interface YearZeroEncounter {
  id: string;
  name: string;
  description: string;
  encounterType: 'combat' | 'exploration' | 'social' | 'survival' | 'mystery';
  difficulty: number; // 1-5
  karmaInfluence: 'positive' | 'negative' | 'neutral';
  possibleOutcomes: EncounterOutcome[];
  requiredSkills?: string[];
  environmentalHazards?: EnvironmentalHazard[];
  loot?: YearZeroItem[];
}

export interface EncounterOutcome {
  condition: string; // What needs to happen for this outcome
  description: string;
  karmaChange: number;
  stressChange: number;
  consequences: string[];
  rewards?: string[];
}

export interface EnvironmentalHazard {
  name: string;
  description: string;
  skillCheck: string; // Skill needed to overcome
  difficulty: number;
  failureConsequence: string;
  stressDamage: number;
}

class YearZeroEngineService {
  /**
   * Creates a character sheet adapted for cyberpunk setting
   */
  createCharacter(name: string, archetype: string, initialKarma: number = 0): YearZeroCharacter {
    const baseAttributes = this.getArchetypeAttributes(archetype);
    const baseSkills = this.getArchetypeSkills(archetype);
    
    return {
      id: uuidv4(),
      name,
      attributes: baseAttributes,
      skills: baseSkills,
      stress: 0,
      trauma: [],
      mutations: this.getInitialMutations(archetype, initialKarma),
      equipment: this.getStartingEquipment(archetype),
      karmaAlignment: initialKarma,
      experience: 0,
      storyProgression: 0
    };
  }

  /**
   * Roll dice using Year Zero mechanics
   */
  rollDice(
    attribute: number, 
    skill: number, 
    gear: number = 0, 
    karmaBonus: number = 0,
    push: boolean = false
  ): DiceRoll {
    const totalDice = attribute + skill + gear + karmaBonus;
    const results: number[] = [];
    
    for (let i = 0; i < totalDice; i++) {
      results.push(Math.floor(Math.random() * 6) + 1);
    }
    
    const successes = results.filter(r => r === 6).length;
    const ones = results.filter(r => r === 1).length;
    
    return {
      dice: totalDice,
      successes,
      ones,
      total: results.reduce((sum, r) => sum + r, 0),
      isPushed: push,
      description: `Rolled ${totalDice}d6: [${results.join(', ')}] - ${successes} successes, ${ones} ones`
    };
  }

  /**
   * Apply stress from choices or failures
   */
  applyStress(character: YearZeroCharacter, amount: number, reason: string): YearZeroCharacter {
    const newStress = Math.min(character.stress + amount, 20); // Cap at 20
    const updatedCharacter = { ...character, stress: newStress };
    
    // Check for trauma at stress thresholds
    if (newStress >= 10 && character.stress < 10) {
      const trauma = this.generateTrauma(character.karmaAlignment, reason);
      updatedCharacter.trauma = [...character.trauma, trauma];
    }
    
    return updatedCharacter;
  }

  /**
   * Generate karma-influenced trauma
   */
  generateTrauma(karma: number, cause: string): YearZeroTrauma {
    const traumaOptions = {
      positive: [
        { name: 'Savior Complex', description: 'Compulsively tries to help everyone', effect: '-1 die to Wits when not helping others' },
        { name: 'Digital Empathy Overload', description: 'Feels others\' pain through the network', effect: '+1 stress when witnessing others\' suffering' },
        { name: 'Pacifist Protocols', description: 'Refuses to cause direct harm', effect: '-2 dice to all attack rolls' }
      ],
      negative: [
        { name: 'Cyber-Paranoia', description: 'Believes everyone is watching through the network', effect: '-1 die to Empathy rolls with strangers' },
        { name: 'Data Addiction', description: 'Compulsively consumes information', effect: '+1 stress when disconnected from networks' },
        { name: 'Corporate Conditioning', description: 'Instinctively follows authority', effect: '-2 dice when opposing authority figures' }
      ],
      neutral: [
        { name: 'Analysis Paralysis', description: 'Overthinks every decision', effect: '+1 stress when making quick decisions' },
        { name: 'Temporal Displacement', description: 'Sometimes loses track of time', effect: 'Occasionally misses important timing' },
        { name: 'Network Static', description: 'Intermittent connection issues', effect: 'Random tech malfunctions' }
      ]
    };
    
    const category = karma > 20 ? 'positive' : karma < -20 ? 'negative' : 'neutral';
    const options = traumaOptions[category];
    const trauma = options[Math.floor(Math.random() * options.length)];
    
    return {
      id: uuidv4(),
      name: trauma.name,
      description: trauma.description,
      effect: trauma.effect,
      karmaThreshold: karma,
      isActive: true,
      causedBy: cause
    };
  }

  /**
   * Evolve mutations based on karma and story progression
   */
  evolveMutations(character: YearZeroCharacter): YearZeroCharacter {
    const updatedMutations = character.mutations.map(mutation => {
      const inRange = character.karmaAlignment >= mutation.karmaRange[0] && 
                     character.karmaAlignment <= mutation.karmaRange[1];
      
      if (inRange && character.storyProgression > mutation.evolutionLevel * 10) {
        return {
          ...mutation,
          evolutionLevel: Math.min(mutation.evolutionLevel + 1, 5),
          description: this.upgradeMutationDescription(mutation)
        };
      }
      
      return { ...mutation, isActive: inRange };
    });
    
    return { ...character, mutations: updatedMutations };
  }

  /**
   * Generate encounter based on karma and environment
   */
  generateEncounter(
    karma: number, 
    location: string, 
    storyProgression: number
  ): YearZeroEncounter {
    const encounterTypes: YearZeroEncounter['encounterType'][] = 
      ['combat', 'exploration', 'social', 'survival', 'mystery'];
    
    const encounterType = encounterTypes[Math.floor(Math.random() * encounterTypes.length)];
    const difficulty = Math.min(Math.floor(storyProgression / 20) + 1, 5);
    
    const encounters = this.getEncounterTemplates(encounterType, location, karma);
    const baseEncounter = encounters[Math.floor(Math.random() * encounters.length)];
    
    return {
      ...baseEncounter,
      id: uuidv4(),
      difficulty,
      karmaInfluence: karma > 0 ? 'positive' : karma < 0 ? 'negative' : 'neutral'
    };
  }

  /**
   * Test skill with karma modifiers
   */
  testSkill(
    character: YearZeroCharacter, 
    skillName: string, 
    difficulty: number = 1,
    situationalModifier: number = 0
  ): { success: boolean; roll: DiceRoll; stressGained: number } {
    const skill = character.skills.find(s => s.name === skillName);
    const attribute = character.attributes.find(a => a.name === skill?.attribute);
    
    if (!skill || !attribute) {
      throw new Error(`Skill ${skillName} not found`);
    }
    
    const karmaBonus = this.getKarmaBonus(character.karmaAlignment, skill);
    const stressModifier = Math.floor(character.stress / 5); // -1 die per 5 stress
    
    const roll = this.rollDice(
      attribute.current,
      skill.value,
      0,
      karmaBonus + situationalModifier - stressModifier
    );
    
    const success = roll.successes >= difficulty;
    const stressGained = success ? 0 : roll.ones; // Gain stress equal to 1s rolled on failure
    
    return {
      success,
      roll,
      stressGained
    };
  }

  /**
   * Apply karma-based equipment bonuses
   */
  getEquipmentBonus(item: YearZeroItem, userKarma: number): number {
    if (!item.karmaResonance) return item.bonus;
    
    const resonance = Math.abs(userKarma - item.karmaResonance);
    
    if (resonance <= 10) return item.bonus + 1; // Perfect alignment
    if (resonance <= 20) return item.bonus;     // Good alignment
    if (resonance <= 40) return Math.max(item.bonus - 1, 0); // Poor alignment
    return 0; // Completely misaligned - item doesn't work
  }

  /**
   * Generate loot based on karma and location
   */
  generateLoot(karma: number, location: string, difficulty: number): YearZeroItem[] {
    const lootTables = this.getLootTables(location);
    const karmaInfluence = karma > 0 ? 'holy' : karma < 0 ? 'corrupted' : 'neutral';
    
    const numItems = Math.floor(Math.random() * difficulty) + 1;
    const loot: YearZeroItem[] = [];
    
    for (let i = 0; i < numItems; i++) {
      const item = this.generateRandomItem(lootTables, karmaInfluence);
      loot.push(item);
    }
    
    return loot;
  }

  // Private helper methods
  private getArchetypeAttributes(archetype: string): YearZeroAttribute[] {
    const attributeDistributions = {
      'cyberpunk-hacker': { Strength: 2, Agility: 3, Wits: 4, Empathy: 3 },
      'street-samurai': { Strength: 4, Agility: 4, Wits: 2, Empathy: 2 },
      'corpo-negotiator': { Strength: 2, Agility: 2, Wits: 4, Empathy: 4 },
      'techno-shaman': { Strength: 2, Agility: 3, Wits: 3, Empathy: 4 }
    };
    
    const distribution = attributeDistributions[archetype] || attributeDistributions['cyberpunk-hacker'];
    
    return [
      { name: 'Strength', value: distribution.Strength, current: distribution.Strength },
      { name: 'Agility', value: distribution.Agility, current: distribution.Agility },
      { name: 'Wits', value: distribution.Wits, current: distribution.Wits },
      { name: 'Empathy', value: distribution.Empathy, current: distribution.Empathy }
    ];
  }

  private getArchetypeSkills(archetype: string): YearZeroSkill[] {
    const skillSets = {
      'cyberpunk-hacker': [
        { name: 'Hacking', value: 3, attribute: 'Wits' as const, description: 'Breach digital systems and manipulate code' },
        { name: 'Electronics', value: 2, attribute: 'Wits' as const, description: 'Understand and repair electronic devices' },
        { name: 'Stealth', value: 2, attribute: 'Agility' as const, description: 'Move unseen and unheard' },
        { name: 'Insight', value: 2, attribute: 'Empathy' as const, description: 'Read people and situations' },
        { name: 'Endure', value: 1, attribute: 'Strength' as const, description: 'Resist physical and mental hardship' }
      ],
      'street-samurai': [
        { name: 'Combat', value: 3, attribute: 'Strength' as const, description: 'Fight with weapons and bare hands' },
        { name: 'Athletics', value: 3, attribute: 'Agility' as const, description: 'Climb, jump, swim, and parkour' },
        { name: 'Intimidation', value: 2, attribute: 'Strength' as const, description: 'Scare or coerce others' },
        { name: 'Streetwise', value: 2, attribute: 'Empathy' as const, description: 'Navigate urban environments and culture' },
        { name: 'Survival', value: 1, attribute: 'Wits' as const, description: 'Survive in harsh environments' }
      ]
    };
    
    return skillSets[archetype] || skillSets['cyberpunk-hacker'];
  }

  private getInitialMutations(archetype: string, karma: number): YearZeroMutation[] {
    const mutations: YearZeroMutation[] = [
      {
        id: uuidv4(),
        name: 'Neural Interface',
        description: 'Cybernetic implant allowing direct brain-computer interface',
        effect: '+1 die to Hacking when directly connected',
        karmaRange: [-50, 50],
        evolutionLevel: 1,
        isActive: true
      }
    ];
    
    // Add karma-specific starting mutations
    if (karma > 20) {
      mutations.push({
        id: uuidv4(),
        name: 'Empathic Resonance',
        description: 'Feel others\' emotions through digital networks',
        effect: '+1 die to Insight, +1 stress when others suffer',
        karmaRange: [20, 100],
        evolutionLevel: 1,
        isActive: true
      });
    } else if (karma < -20) {
      mutations.push({
        id: uuidv4(),
        name: 'Digital Predator',
        description: 'Enhanced ability to exploit system vulnerabilities',
        effect: '+1 die to Hacking against security, -1 die to Empathy',
        karmaRange: [-100, -20],
        evolutionLevel: 1,
        isActive: true
      });
    }
    
    return mutations;
  }

  private getStartingEquipment(archetype: string): YearZeroItem[] {
    const equipmentSets = {
      'cyberpunk-hacker': [
        {
          id: uuidv4(),
          name: 'Cyberdeck',
          description: 'Portable hacking computer with neural interface',
          itemType: 'cyberware' as const,
          bonus: 2,
          reliability: 5,
          karmaResonance: 0,
          yearZeroRating: 4,
          specialAbilities: ['Network Access', 'ICE Breaking'],
          condition: 'intact' as const
        },
        {
          id: uuidv4(),
          name: 'Stealth Suit',
          description: 'Light armor with camouflage capabilities',
          itemType: 'armor' as const,
          bonus: 1,
          reliability: 4,
          yearZeroRating: 3,
          specialAbilities: ['Camouflage'],
          condition: 'intact' as const
        }
      ]
    };
    
    return equipmentSets[archetype] || equipmentSets['cyberpunk-hacker'];
  }

  private getKarmaBonus(karma: number, skill: YearZeroSkill): number {
    if (!skill.karmaModifier) return 0;
    
    const alignment = karma > 0 ? 1 : karma < 0 ? -1 : 0;
    return alignment === Math.sign(skill.karmaModifier) ? 1 : -1;
  }

  private upgradeMutationDescription(mutation: YearZeroMutation): string {
    const upgrades = {
      'Neural Interface': [
        'Basic brain-computer link',
        'Enhanced processing bandwidth',
        'Multi-threaded consciousness',
        'Quantum-encrypted thought streams',
        'Transcendent digital awareness'
      ]
    };
    
    const descriptions = upgrades[mutation.name] || [mutation.description];
    return descriptions[Math.min(mutation.evolutionLevel - 1, descriptions.length - 1)];
  }

  private getEncounterTemplates(type: string, location: string, karma: number): Partial<YearZeroEncounter>[] {
    // This would be a large database of encounter templates
    // Returning simplified examples for now
    return [
      {
        name: `${type} encounter in ${location}`,
        description: `A ${type} challenge influenced by karma level ${karma}`,
        possibleOutcomes: [
          {
            condition: 'Success with 3+ successes',
            description: 'Complete success',
            karmaChange: karma > 0 ? 2 : -1,
            stressChange: -1,
            consequences: ['Gain advantage in next encounter']
          }
        ]
      }
    ];
  }

  private getLootTables(location: string): any {
    // Simplified loot table
    return {
      common: ['Scrap Metal', 'Data Chips', 'Circuit Boards'],
      rare: ['Military Cyberware', 'Quantum Processors', 'AI Cores'],
      legendary: ['Sentient Weapons', 'Reality Manipulation Devices']
    };
  }

  private generateRandomItem(lootTables: any, karmaInfluence: string): YearZeroItem {
    const rarity = Math.random() < 0.1 ? 'legendary' : Math.random() < 0.3 ? 'rare' : 'common';
    const itemName = lootTables[rarity][Math.floor(Math.random() * lootTables[rarity].length)];
    
    return {
      id: uuidv4(),
      name: `${karmaInfluence} ${itemName}`,
      description: `A ${karmaInfluence}-influenced ${itemName.toLowerCase()}`,
      itemType: 'gear',
      bonus: rarity === 'legendary' ? 3 : rarity === 'rare' ? 2 : 1,
      reliability: Math.floor(Math.random() * 6) + 1,
      yearZeroRating: rarity === 'legendary' ? 5 : rarity === 'rare' ? 3 : 1,
      specialAbilities: [],
      condition: 'intact'
    };
  }
}

export const yearZeroEngine = new YearZeroEngineService();